import {
  ShieldCheck,
  Shield,
  AlertCircle,
  AlertTriangle,
  AlertOctagon,
  HelpCircle,
  type LucideIcon,
} from 'lucide-react';

/**
 * Display-only contract + presentation map for the Organ Health Module.
 *
 * This mirrors the backend `OrganScore` JSON (see
 * `src/march/risk_engine/organ_health/scorer.py`). The frontend NEVER computes a
 * score, band, penalty, or cap — every number arrives already computed. This
 * module only maps a band to how it looks and reads (colour + icon + glyph +
 * label + plain-language meaning), so severity is never conveyed by colour
 * alone — every band carries a glyph, and severe/moderate additionally carry
 * a hatch pattern, per the design system's rule that colour is never the sole
 * carrier of meaning (this matters more in dark theme, not less).
 */

export type OrganName =
  | 'kidney'
  | 'liver'
  | 'lung'
  | 'heart'
  | 'brain'
  // v0.12 endocrine organs (6-10).
  | 'thyroid'
  | 'adrenal'
  | 'pituitary'
  | 'pancreas'
  | 'gonad'
  | 'overall';

/** v0.12. The COMPOSITE's own vocabulary, deliberately disjoint from
 * OrganBand. Severity says how damaged the organ is (a statement about
 * function); priority says how hard to work the problem (a statement about the
 * composite, which risk can move). Sharing one word list would let a clinician
 * read "severe" off a number that risk pulled down, which is exactly the
 * confusion the v0.12 contract exists to prevent. */
export type OrganPriority =
  | 'routine'
  | 'monitor'
  | 'active management'
  | 'intensive'
  | 'acute'
  | 'unknown';
export type OrganBand = 'healthy' | 'mild' | 'moderate' | 'severe' | 'critical' | 'unknown';
export type OrganConfidence = 'high' | 'moderate' | 'low';

/** Risk-track bands (rubric v0.10). Higher risk_score = BETTER care, so
 * 'excellent' sits at the top of the 0-100 range and 'critical' at the bottom —
 * the opposite polarity from OrganBand, which is why they are separate types.
 * The two multi-word values carry their space; they are the wire contract. */
export type OrganRiskBand =
  | 'excellent'
  | 'good'
  | 'moderate risk'
  | 'high risk'
  | 'critical'
  | 'unknown';

/** Provenance + audit for the per-organ prose summary. Mirrors the SDOH
 * summary contract (sdohApi.SdohSummaryMeta): the text restates the stored
 * score row and is either LLM-composed-and-audited or the deterministic
 * fallback, always saying which. */
export interface OrganSummaryMeta {
  text: string;
  mode: string;
  model_id?: string | null;
  judge_verdict?: string | null;
  confidence: 'high' | 'moderate' | 'low' | string;
  confidence_note?: string | null;
  evidence_hash: string;
  generated_at?: string | null;
  stale?: boolean;
}

export interface OrganScore {
  organ: OrganName;
  /** The HEADLINE: function_score minus the bounded risk penalty. */
  score: number | null;
  /** SEVERITY. v0.12: derived from function_score ALONE, so no risk profile
   * can move this word. */
  band: OrganBand;
  /** MANAGEMENT POSTURE for the composite score. v0.12: risk moves the
   * headline number, so it moves priority — but never `band`. */
  priority?: OrganPriority;
  /** WHY this organ could not be scored. Present only when `score` is null.
   * Rendered in place of the placeholder dash so an unscorable card names the
   * missing test rather than leaving the clinician to guess. */
  not_scorable_reason?: string | null;
  confidence: OrganConfidence;
  /** Current function and staging, capped by any acute red flag. Retained
   * separately because it and risk_score tell different stories — a heart can
   * be the best-functioning organ a patient has and carry the worst risk. */
  function_score?: number | null;
  /** Modifiable risk for FUTURE disease of this organ. Higher = lower risk. */
  risk_score?: number | null;
  /** Stored per-organ prose summary — absent on older payloads. */
  summary?: OrganSummaryMeta | null;
  risk_band?: OrganRiskBand;
  red_flags: string[];
  to_improve: string[];
  structured_fields?: {
    /** v0.10 function track: the base anchor and each applied modifier. */
    function?: {
      anchor?: { param: string; stage: string; base: number };
      modifiers?: Record<string, { points: number; band: string }>;
      /** Inputs with no data — recorded so an absent modifier is visibly
       * absent rather than silently scored as its best rung. */
      absent?: string[];
      synthetic_levels?: Record<string, number>;
      hfpef_rule_applied?: boolean;
      moca_education_adjusted?: { raw: number; adjusted: number };
    };
    /** v0.10 risk track: per-domain earned/available points and factor detail. */
    risk_track?: {
      risk_score: number | null;
      risk_band: OrganRiskBand;
      confidence: OrganConfidence;
      domains?: Record<
        string,
        {
          label: string;
          earned: number;
          available_max: number;
          declared_max: number;
          factors: Record<
            string,
            { points: number | null; max: number; band?: string; missing?: boolean }
          >;
        }
      >;
      missing_factors?: string[];
      to_improve?: string[];
      /** Fraction of the rubric's declared maximum that was actually scored. */
      coverage?: number;
      /** True when the score was WITHHELD because coverage fell below the floor
       * — distinct from nothing having been measured at all. */
      insufficient_coverage?: boolean;
    };
    /** The bounded deduction risk applied to the headline (0-15). */
    risk_penalty?: number;
    /** True when a red flag capped function — the penalty is then suppressed. */
    capped_by_red_flag?: boolean;
    /** A flag capped the score with no gradable baseline (acute state only). */
    red_flag_capped_without_baseline?: boolean;
    /** Legacy (rubric <= 0.6, method transfer_v1). Kept so historical rows still
     * render; never populated by the anchor scorer. */
    subscores?: Record<string, number>;
    penalties_applied?: Record<string, number>;
    base?: number;
    caps_applied?: number[];
    reason?: string;
    method_version?: string;
    /** The raw clinical values that fed this score (eGFR 38, UACR 620, K 6.8…),
     * each with its recorded date when known. Populated on scores built after
     * the input-provenance change; absent on older rows (UI degrades). */
    inputs?: Record<string, { value: number; date?: string; derived?: string }>;
    /** Coded findings present at scoring time (e.g. ecg_changes, ascites) —
     * these are the FHIR conditions/imaging that fed the score. */
    findings_present?: string[];
    /** Acute trend signals the caps consumed (aki, aki_severe, spo2_drop). */
    trends_present?: string[];
    /** Overall roll-up only: which graded organ anchors the score (the worst). */
    anchored_on?: string;
    /** Overall roll-up only: every graded organ's score, for the breakdown. */
    organ_scores?: Record<string, number>;
    /** Overall roll-up only: validated prognostic indices (FIB-4/MELD/KFRE)
     * computed by organ_health.progression. Display-only; KFRE carries its own
     * `provisional` flag. Absent when no calculator's inputs were present. */
    progression?: Record<
      string,
      {
        value: number | string | null;
        category: string | null;
        units?: string;
        horizon_years?: number;
        provisional?: boolean;
      }
    >;
    /** Overall roll-up only: advisory, NON-DIRECTIVE guideline-therapy gaps
     * (organ_health.gdmt, flag-gated). Empty/absent unless enabled. */
    gdmt_gaps?: Array<{
      therapy_class: string;
      indication: string;
      status: string;
      directive?: boolean;
      provisional?: boolean;
      guideline: string;
      advisory: string;
    }>;
  };
  rubric_version?: string;
  method_version?: string;
}

export const ORGAN_LABEL: Record<OrganName, string> = {
  kidney: 'Kidney',
  liver: 'Liver',
  lung: 'Lung',
  heart: 'Heart',
  brain: 'Brain',
  thyroid: 'Thyroid',
  adrenal: 'Adrenal',
  pituitary: 'Pituitary',
  pancreas: 'Pancreas',
  // "Gonad" is the rubric's term; the card is scored from sex-specific tables
  // and the anatomy is never inferred, so the label stays neutral.
  gonad: 'Gonadal',
  overall: 'Overall',
};

/** Priority presentation. The load-bearing rule is the SHAPE, not the colour:
 * a BandChip is always FILLED (tinted band accent), a PriorityChip is always
 * OUTLINED. That invariant is what keeps a clinician from reading a management
 * posture as a severity grade at a glance, and it holds even for 'acute',
 * where the urgency is carried by border and text intensity rather than by
 * borrowing the severity ramp. Every level also carries an icon and a word, so
 * nothing here is colour-only. */
export const PRIORITY_STYLES: Record<
  OrganPriority,
  { outline: string; icon: 'check' | 'eye' | 'clipboard' | 'alert' }
> = {
  routine: { outline: 'border-border text-muted-foreground', icon: 'check' },
  monitor: { outline: 'border-border text-foreground', icon: 'eye' },
  'active management': {
    outline: 'border-border text-foreground',
    icon: 'clipboard',
  },
  intensive: {
    outline: 'border-(--warning) text-(--warning)',
    icon: 'alert',
  },
  acute: {
    outline: 'border-(--critical) text-(--critical)',
    icon: 'alert',
  },
  unknown: { outline: 'border-dashed border-border text-muted-foreground', icon: 'check' },
};

/** Priority is about EFFORT, never about damage. Each label is a management
 * posture; none of them is a severity word. */
/** Why an organ could not be scored, in words a clinician can act on.
 * "Awaiting data" is true but useless — it does not say WHICH data. These map
 * the scorer's `not_scorable_reason` onto the missing test, so an unscorable
 * card names its own gap. */
/** How much visual weight each confidence level earns.
 *
 * These deliberately are NOT uniform. The module's standing rule is that
 * missing data lowers CONFIDENCE and never the score — which means a
 * low-confidence 92 and a high-confidence 92 are the same number carrying very
 * different weight, and rendering both in identical grey hides exactly the
 * distinction the rule exists to preserve. Low earns an icon and a warning
 * tone; high stays quiet, because a well-evidenced score should not nag. */
export const CONFIDENCE_EMPHASIS: Record<
  OrganConfidence,
  { className: string; marked: boolean }
> = {
  high: { className: 'text-text-tertiary', marked: false },
  moderate: { className: 'text-text-secondary', marked: false },
  low: { className: 'text-(--warning) font-medium', marked: true },
};

export const NOT_SCORABLE_LABEL: Record<string, string> = {
  // The five original organs' reasons were absent here while the v0.12
  // endocrine ones were present, so a clinician read the raw code with its
  // underscores combed out ("liver incomplete synthetic panel") as if it were
  // prose. test_organ_not_scorable_labels_v1.py fails if either side of the
  // wire emits a reason the other cannot name.
  kidney_no_egfr: 'Needs an eGFR to stage kidney function',
  liver_incomplete_synthetic_panel: 'Needs the full synthetic panel — albumin, INR and bilirubin',
  lung_no_spirometry: 'Needs spirometry — FEV1/FVC, then FEV1 % predicted',
  lung_obstruction_ungraded: 'Obstruction confirmed — needs FEV1 % predicted to grade severity',
  lung_non_obstructive_ungraded: 'No obstruction — needs FEV1 % predicted to tell normal from restrictive',
  lung_non_obstructive_reduced: 'Non-obstructive with reduced FEV1 % predicted — GOLD does not stage this pattern',
  // Retained so rows written before the lung split still read as words.
  non_obstructive: 'No obstruction on spirometry — GOLD staging does not apply',
  heart_no_lvef: 'Needs an LVEF (echocardiography)',
  brain_no_cognitive_scale: 'Needs a cognitive scale (MoCA/MMSE)',
  not_gradable: 'Not gradable from the values on file',
  gonad_needs_gonadal_anatomy: 'Needs documented gonadal anatomy',
  // Says what is not validated, not what the patient is. The card must read as
  // a limit of the rubric — because that is what it is — and never as a remark
  // about the person in front of the clinician.
  gonad_not_validated_for_gender_affirming_therapy:
    'Not validated for gender-affirming hormone therapy — other organs unaffected',
  gonad_needs_confirmed_morning_testosterone: 'Needs a morning testosterone, confirmed on a second sample',
  gonad_needs_total_testosterone: 'Needs a total testosterone',
  gonad_needs_cycle_status: 'Needs cycle or menopausal status',
  thyroid_needs_tsh_and_ft4: 'Needs TSH and free T4 from the same draw',
  thyroid_central_pattern_score_pituitary: 'Central pattern — score the pituitary instead',
  adrenal_needs_morning_or_stimulated_cortisol: 'Needs a morning cortisol or a stimulation test',
  pituitary_needs_axis_assessment: 'Needs a pituitary axis assessment',
  pancreas_needs_hba1c: 'Needs an HbA1c',
  insufficient_data: 'Not enough data to score',
};

export function notScorableLabel(reason?: string | null): string | null {
  if (!reason) return null;
  return NOT_SCORABLE_LABEL[reason] ?? reason.replace(/_/g, ' ');
}

/**
 * The one actionable sentence for an organ that could not be scored.
 *
 * The scorer distinguishes two ungraded states on purpose: a NAMED gap
 * (`heart_no_lvef` — "we have some primaries, this one test unlocks the
 * score") and `insufficient_data` (nothing was measured at all). Only the
 * first has a useful label; the generic one renders as "Not enough data to
 * score", which tells a clinician nothing they cannot already see. In that
 * case `to_improve[0]` is the actionable equivalent the scorer already
 * computed — "Add LVEF (echocardiography) to score this organ." — so prefer
 * it, and fall back to the generic label only when nothing better exists.
 */
export function unscorableGap(organ: OrganScore): string | null {
  const reason = organ.not_scorable_reason ?? null;
  if (reason && reason !== 'insufficient_data') return notScorableLabel(reason);
  return organ.to_improve?.[0] ?? notScorableLabel(reason);
}

export const PRIORITY_LABEL: Record<OrganPriority, string> = {
  routine: 'Routine',
  monitor: 'Monitor',
  'active management': 'Active management',
  intensive: 'Intensive',
  acute: 'Acute',
  // NOT "Not scored". This chip renders beside the band chip, so on a scored
  // organ it produced "78/100 · Mild · Not scored" -- two chips, opposite
  // claims, one card. It has never been about the score.
  unknown: 'Priority not set',
};

/** The single-organ cards, in a fixed clinical order. 'overall' is the
 * worst-organ-anchored roll-up, rendered in its own reserved slot. */
export const ORGAN_ORDER: OrganName[] = [
  'kidney',
  'liver',
  'lung',
  'heart',
  'brain',
  'thyroid',
  'adrenal',
  'pituitary',
  'pancreas',
  'gonad',
];

/** Risk-band presentation. Deliberately NOT reusing BAND_STYLE: a risk score of
 * 25 and a function score of 25 are both alarming, but they mean different
 * things and demand different action (optimize modifiable factors vs. treat
 * established disease). Giving them identical chrome would invite a clinician
 * to read one as the other. */
export const RISK_BAND_LABEL: Record<OrganRiskBand, string> = {
  excellent: 'Excellent',
  good: 'Good',
  'moderate risk': 'Moderate risk',
  'high risk': 'High risk',
  critical: 'Critical risk',
  // Qualified, so it cannot be read as "this organ has no score at all" while
  // the function track carries one.
  unknown: 'Risk not scored',
};

/** Human labels for the risk-track factor keys (mirrors risk_track.py's
 * _FACTOR_LABELS). Kept in sync by `test_risk_factor_labels_cover_every_factor`
 * on the backend, which fails if config grows a factor neither side names. */
export const RISK_FACTOR_LABEL: Record<string, string> = {
  anabolic_steroid_misuse: 'Anabolic steroid / androgen misuse',
  chemo_pelvic_radiation: 'Chemotherapy / pelvic radiation',
  occupational_heat_solvent: 'Occupational heat / solvent exposure',
  bmi_adiposity: 'BMI / adiposity',
  glycemic_self_management: 'Glycemic self-management',
  triglycerides: 'Triglycerides',
  gallstone_disease: 'Gallstone disease',
  surveillance_imaging_currency: 'Surveillance imaging currency',
  sick_day_crisis_preparedness: 'Sick-day / crisis preparedness',
  cranial_radiation_history: 'Cranial radiation history',
  tbi_history: 'Traumatic brain injury history',
  exogenous_glucocorticoid: 'Exogenous glucocorticoid',
  opioid_suppressive_drug: 'Opioid / other suppressive drug',
  checkpoint_immunotherapy: 'Checkpoint inhibitor / immunotherapy',
  sick_day_rules: 'Sick-day rules education',
  emergency_hydrocortisone_alert_id: 'Emergency hydrocortisone / alert ID',
  replacement_adherence: 'Replacement adherence',
  axis_electrolyte_monitoring: 'Axis and electrolyte monitoring',
  thyroid_antibodies: 'TPO / TRAb antibodies',
  family_history: 'Family history',
  other_autoimmune_disease: 'Other autoimmune disease',
  iodine_status: 'Iodine status / nutrition',
  neck_radiation_history: 'Neck radiation history',
  drug_exposure: 'Drug exposure',
  monitoring_adherence: 'Monitoring / adherence',
  blood_pressure: 'Blood pressure',
  glucose: 'Glucose / HbA1c',
  bmi: 'BMI',
  bmi_waist: 'BMI / waist',
  bmi_nutrition: 'BMI / nutrition',
  bmi_central_adiposity: 'BMI / central adiposity',
  ldl: 'LDL cholesterol',
  nephrotoxin_exposure: 'Nephrotoxin exposure',
  smoking: 'Smoking',
  tobacco_smoking: 'Tobacco smoking',
  diet_sodium_protein: 'Diet · sodium / protein',
  diet_quality: 'Diet quality',
  physical_activity: 'Physical activity',
  family_genetic_risk: 'Family / genetic risk',
  triglycerides_mets: 'Triglycerides / MetS',
  alcohol: 'Alcohol',
  viral_hepatitis: 'Viral hepatitis',
  hepatotoxic_meds: 'Hepatotoxic medications',
  occupational_environmental: 'Occupational / environmental',
  secondhand_smoke: 'Secondhand smoke / air quality',
  vaccination: 'Vaccination',
  exacerbations_12mo: 'Exacerbations · past 12 mo',
  stroke_tbi_history: 'Stroke / TBI history',
  depression: 'Depression (PHQ-2/9)',
  sleep_duration: 'Sleep duration',
  social_engagement: 'Social engagement',
  education_reserve: 'Education / reserve',
  hearing_vision: 'Hearing / vision',
};

export function riskFactorLabel(key: string): string {
  return RISK_FACTOR_LABEL[key] ?? key.replace(/_/g, ' ');
}

export const RISK_BAND_MEANING: Record<OrganRiskBand, string> = {
  excellent: 'Low modifiable risk; maintain, routine screening by age.',
  good: 'One or more suboptimal factors; targeted counseling.',
  'moderate risk': 'Multiple risk factors; active risk-factor management.',
  'high risk': 'Aggressive optimization; specialist referral as indicated.',
  critical: 'Dense clustering of unmodified risk; comprehensive review.',
  unknown: 'Not enough recorded risk factors to score this track.',
};

export interface BandStyle {
  /** Human label shown alongside the colour (never colour-only). */
  label: string;
  /** One-line plain meaning for the detail view. */
  meaning: string;
  icon: LucideIcon;
  /** Mandatory non-colour encoding — escalating chevrons, then a solid block
   * reserved for critical. Empty for the healthy band (nothing to flag). */
  glyph: string;
  /** Card / chip surface classes — read the `--band-*` tokens, never a raw
   * Tailwind palette colour or a `dark:` conditional (both themes already
   * resolve these). */
  surface: string;
  /** Foreground accent for text + icon, on the tint. */
  accent: string;
  /** Solid marker/track fill used on the band scale and score chip. */
  fill: string;
  /** Readable text-on-solid-fill colour (for a filled badge, not a tint). */
  onFill: string;
  /** Moderate/severe additionally carry a hatch pattern — a non-colour
   * texture cue, not decorative. Undefined for bands that don't need one. */
  hatch?: string;
}

/**
 * Band presentation. Colours + glyphs follow the token system's severity
 * ramp: Healthy (no glyph) · Mild ▾ · Moderate ▾▾ (+ hatch) · Severe ▾▾▾
 * (+ hatch) · Critical ▮. Hue walks 165°→120°→75°→40°→25°, lightness tuned
 * per theme so the ramp survives inversion — see globals.css.
 */
export const BAND_STYLES: Record<OrganBand, BandStyle> = {
  healthy: {
    label: 'Healthy',
    meaning: 'Function within the normal range.',
    icon: ShieldCheck,
    glyph: '',
    // Card surface stays neutral (bg-surface-raised, applied by the card
    // component) — only the border carries a hint of band color. The -bg
    // tint token is for small chip pills, not a full-card wash: at chip
    // size it reads as a badge; flood-filled across a whole card it drowns
    // the card in color and (in dark mode, where -bg sits lighter than
    // surface-raised) looks like a lit-up smear rather than a quiet tint.
    surface: 'border-band-normal/30',
    accent: 'text-band-normal-text',
    fill: 'bg-band-normal',
    onFill: 'text-band-normal-on',
  },
  mild: {
    label: 'Mild',
    meaning: 'Earliest measurable reduction; monitor.',
    icon: Shield,
    glyph: '▾',
    surface: 'border-band-mild/30',
    accent: 'text-band-mild-text',
    fill: 'bg-band-mild',
    onFill: 'text-band-mild-on',
  },
  moderate: {
    label: 'Moderate',
    meaning: 'Clearly reduced function.',
    icon: AlertCircle,
    glyph: '▾▾',
    surface: 'border-band-moderate/30',
    accent: 'text-band-moderate-text',
    fill: 'bg-band-moderate',
    onFill: 'text-band-moderate-on',
    hatch: 'var(--band-moderate-hatch)',
  },
  severe: {
    label: 'Severe',
    meaning: 'Function is at the door of decompensation.',
    icon: AlertTriangle,
    glyph: '▾▾▾',
    surface: 'border-band-severe/30',
    accent: 'text-band-severe-text',
    fill: 'bg-band-severe',
    onFill: 'text-band-severe-on',
    hatch: 'var(--band-severe-hatch)',
  },
  critical: {
    label: 'Critical',
    meaning: 'Established failure or critical range.',
    icon: AlertOctagon,
    glyph: '▮',
    surface: 'border-band-critical/30',
    accent: 'text-band-critical-text',
    fill: 'bg-band-critical',
    onFill: 'text-band-critical-on',
  },
  // Not a severity band — the neutral/disabled treatment for "not enough
  // data to grade this organ". No glyph (there's no severity to encode),
  // no --band-* token (colour is reserved for organ scores that HAVE a
  // score; this one deliberately doesn't).
  unknown: {
    label: 'Unknown',
    meaning: 'Not enough data to grade this organ.',
    icon: HelpCircle,
    glyph: '',
    // No bg here — the card component owns the surface (bg-surface-raised);
    // a no-data card sits on the normal card ground, differentiated by its
    // dashed chip, not a second background colour.
    surface: 'border-border',
    accent: 'text-text-tertiary',
    fill: 'bg-border-strong',
    onFill: 'text-text-primary',
  },
};

export const CONFIDENCE_LABEL: Record<OrganConfidence, string> = {
  high: 'High confidence',
  moderate: 'Moderate confidence',
  low: 'Low confidence',
};

/** The five graded bands, worst → best, for the horizontal scale. */
export const SCALE_BANDS: { band: OrganBand; from: number; to: number }[] = [
  { band: 'critical', from: 0, to: 29 },
  { band: 'severe', from: 30, to: 49 },
  { band: 'moderate', from: 50, to: 69 },
  { band: 'mild', from: 70, to: 89 },
  { band: 'healthy', from: 90, to: 100 },
];

/** Minimum chip area (px) below which colour alone becomes unreliable —
 * dense rows (patient-list tables) must show label + glyph, no colour patch,
 * below this floor. See SPEC.md "Minimum band-chip area." */
export const MIN_COLOR_CHIP_PX = 24;

export function bandStyle(band: OrganBand): BandStyle {
  return BAND_STYLES[band] ?? BAND_STYLES.unknown;
}

/** True when the score was hard-capped by a fired red flag (display-only read). */
export function isRedFlagCapped(organ: OrganScore): boolean {
  return Array.isArray(organ.red_flags) && organ.red_flags.length > 0;
}

/** True for the explicit insufficient-data / non-gradable state. */
export function isInsufficient(organ: OrganScore): boolean {
  return organ.score == null || organ.band === 'unknown';
}

/** Human phrase for a red-flag id (e.g. "inr_synthetic_failure"). */
export function redFlagLabel(id: string): string {
  return id
    .replace(/_/g, ' ')
    .replace(/\bhrs\b/i, 'hepatorenal syndrome')
    .replace(/\baki\b/i, 'AKI')
    .replace(/\binr\b/i, 'INR')
    .replace(/^\w/, (c) => c.toUpperCase());
}

/**
 * Why an organ has no score — three *distinct* clinical states, never folded
 * (§ missing-data: empty ≠ zero ≠ not-gradeable). Derived from the backend's
 * `structured_fields.reason` (see scorer.py), never guessed:
 *   - 'no_data'       — reason 'insufficient_data': zero primaries on record.
 *   - 'not_gradeable' — reason 'non_obstructive'/'not_gradable': data present
 *     but the method deliberately declines to grade (e.g. lung obstruction
 *     gate not passed; grading would misattribute the values).
 *   - 'error'         — reason 'scorer_error': the scorer itself failed.
 * A scored organ returns 'scored'. Fetch failures are an error state owned by
 * the panel, not this per-organ read.
 */
export type OrganGradeState = 'scored' | 'no_data' | 'not_gradeable' | 'error';

/** Reason codes the scorer emits for a lung it could not grade, and whether the
 * organ is "not gradeable" (we know what's missing / it's a real no-obstruction
 * result) vs. genuinely "no data". All three read as not-gradeable — each is an
 * honest, specific state, explained by the organ's `to_improve` line. */
const NOT_GRADEABLE_REASONS = new Set([
  'non_obstructive', // FEV1/FVC ≥ 0.70 — real spirometry result, no obstruction
  'not_gradable', // generic (e.g. kidney without eGFR handled elsewhere)
  'lung_no_spirometry', // no FEV1/FVC on record — the obstruction gate can't run
  'lung_obstruction_ungraded', // obstruction confirmed, FEV1 % predicted missing
]);

export function gradeState(organ: OrganScore): OrganGradeState {
  if (organ.score != null && organ.band !== 'unknown') return 'scored';
  const reason = organ.structured_fields?.reason;
  if (reason && NOT_GRADEABLE_REASONS.has(reason)) return 'not_gradeable';
  if (reason === 'scorer_error') return 'error';
  return 'no_data';
}

/** A short, honest explanation for a non-scored organ's state — surfaced next to
 * the "Not gradeable" chip so the clinician sees WHY at a glance. */
export const GRADE_STATE_REASON: Record<string, string> = {
  non_obstructive: 'FEV1/FVC ≥ 0.70 — no airflow obstruction on spirometry; the COPD score does not apply.',
  lung_no_spirometry: 'No spirometry on record — an obstruction (FEV1/FVC) result is needed to grade the lung.',
  lung_obstruction_ungraded: 'Obstruction confirmed, but FEV1 % predicted is missing — severity can’t be graded yet.',
};

/** The chip word for an organ whose FUNCTION did not score.
 *
 * Every one of these names the track. They used to say "No data" and "Not
 * gradeable" -- statements about the whole organ -- and they rendered on cards
 * that were simultaneously showing "Modifiable risk: High risk 45". A liver
 * chipped "No data" above its own scored risk number tells the clinician the
 * panel is broken, when in fact both halves were right about different tracks.
 *
 * Risk and function score independently: risk reads lifestyle, vitals and
 * social factors; function needs an anchoring lab. Risk frequently scores when
 * function cannot, so an unqualified word here is wrong far more often than it
 * is right. This wording matches the clinician summary panel, which already
 * says "Function not scored" -- two views of one patient must not use two
 * vocabularies for one state. */
export const GRADE_STATE_LABEL: Record<Exclude<OrganGradeState, 'scored'>, string> = {
  no_data: 'Function not scored',
  not_gradeable: 'Function not gradeable',
  error: "Couldn't score",
};

/** Short human labels for the per-parameter sub-scores the scorer persists in
 * `structured_fields.subscores` (0–100 transfer-curve outputs, NOT raw labs —
 * the raw values aren't sent, so we never display them). Keys mirror
 * scorer.py's transfer-test names. */
export const SUBSCORE_LABEL: Record<string, string> = {
  egfr: 'eGFR',
  uacr: 'UACR',
  albumin: 'albumin',
  inr: 'INR',
  bilirubin: 'bilirubin',
  platelets: 'platelets',
  fibrosis: 'fibrosis',
  fev1_pct: 'FEV1 %',
  spo2: 'SpO₂',
  dlco: 'DLCO',
  dlco_pct: 'DLCO',
  fev1_fvc_ratio: 'FEV1/FVC',
  lvef: 'LVEF',
  nt_probnp: 'NT-proBNP',
  nyha: 'NYHA',
  moca: 'MoCA',
  mrs: 'mRS',
  gcs: 'GCS',
  synthetic_points: 'synthetic points',
  actively_progressing: 'active progression',
};

/** Liver and lung combine their functional axes by worst-of (min); the leading
 * (lowest) sub-score is what set the base, so it earns a "leads" marker.
 * Kidney is a subtract model (eGFR curve − UACR penalty), so nothing "leads". */
const WORST_OF_ORGANS: ReadonlySet<OrganName> = new Set(['liver', 'lung']);

export interface SubscoreEntry {
  key: string;
  label: string;
  value: number;
  leads: boolean;
}

/** Per-parameter sub-scores for the detail view, in a stable order, with the
 * worst-of leader marked. Returns [] when the backend sent none. */
export function subscoreEntries(organ: OrganScore): SubscoreEntry[] {
  const subs = organ.structured_fields?.subscores;
  if (!subs || Object.keys(subs).length === 0) return [];
  const entries = Object.entries(subs).map(([key, value]) => ({
    key,
    label: SUBSCORE_LABEL[key] ?? key.replace(/_/g, ' '),
    value: Math.round(value),
  }));
  let leaderKey: string | null = null;
  if (WORST_OF_ORGANS.has(organ.organ) && entries.length > 1) {
    leaderKey = entries.reduce((lo, e) => (e.value < lo.value ? e : lo), entries[0]).key;
  }
  return entries.map((e) => ({ ...e, leads: e.key === leaderKey }));
}

// ---------------------------------------------------------------------------
// Derivation — the honest "why is this the score" chain, reconstructed only
// from what the scorer actually persisted. Every string below describes the
// real methodology (scorer.py), never patient data we weren't given.
// ---------------------------------------------------------------------------

/** The band a raw score falls in, per the fixed scale. */
export function bandForScore(score: number): OrganBand {
  for (const s of SCALE_BANDS) if (score >= s.from && score <= s.to) return s.band;
  return score >= 90 ? 'healthy' : 'critical';
}

/** The numeric range for a band (for the "40 is in Severe (30–49)" wording). */
export function bandRange(band: OrganBand): { from: number; to: number } | null {
  return SCALE_BANDS.find((s) => s.band === band) ?? null;
}

/** What each functional axis measures — the derivation's "what happened" text. */
export const SUBSCORE_MEANING: Record<string, string> = {
  egfr: 'Kidney filtration (eGFR), read off its transfer curve',
  uacr: 'Albuminuria (UACR), read off its transfer curve',
  albumin: 'Synthetic function (albumin), read off its transfer curve',
  inr: 'Clotting (INR), read off its transfer curve',
  bilirubin: 'Excretory function (bilirubin), read off its transfer curve',
  fev1_pct: 'Airflow (FEV1 % predicted), read off its transfer curve',
  spo2: 'Oxygenation (resting SpO₂), read off its transfer curve',
};

/** Per-organ method, described accurately (mirrors scorer.score_function()).
 * Under rubric v0.10 every organ is the same shape — a base anchor from the
 * primary parameter, then bounded modifiers — so the label is uniform and the
 * detail names the actual staging table the number came from. */
export const COMBINE_METHOD: Record<
  Exclude<OrganName, 'overall'>,
  { label: string; detail: string }
> = {
  kidney: {
    label: 'Anchor · KDIGO grid',
    detail: 'eGFR sets the base stage (G1–G5); albuminuria (UACR) is a bounded modifier — never a worst-of.',
  },
  liver: {
    label: 'Anchor · synthetic points',
    detail:
      'Albumin, INR and bilirubin each score 0/1/2 and sum to S0–S6, which sets the base; fibrosis applies one modifier from the worst evidence.',
  },
  lung: {
    label: 'Anchor · GOLD grade',
    detail: 'Obstruction is confirmed first (FEV1/FVC < 0.70); FEV1 % predicted sets the GOLD base, with SpO₂ and DLCO modifiers.',
  },
  heart: {
    label: 'Anchor · LVEF',
    detail: 'LVEF sets the base; NT-proBNP and NYHA class are bounded modifiers. A preserved LVEF does not exclude heart failure.',
  },
  brain: {
    label: 'Anchor · cognition',
    detail: 'A standardized cognitive scale (MoCA/MMSE) sets the base; functional status (mRS) is a bounded modifier.',
  },
  thyroid: {
    label: 'Axis pattern anchor, bounded modifiers',
    detail:
      'TSH and free T4 are read as a PAIR — the axis pattern sets the base, never either value alone. Autoimmunity and structural disease are bounded modifiers. Adequate replacement caps the base at 85: treated disease is not restored function.',
  },
  adrenal: {
    label: 'Glucocorticoid reserve anchor, bounded modifiers',
    detail:
      'A morning cortisol or the ACTH-stimulated peak sets the base; a random cortisol is uninterpretable and never anchors. Electrolytes, aldosteronism and DHEAS are bounded modifiers.',
  },
  pituitary: {
    label: 'Axis-integrity anchor, bounded modifiers',
    detail:
      'Scored by how many of the six axes are intact, never by one hormone, and a deficiency counts only on biochemical evidence. Replacement, tumour status and sodium are bounded modifiers.',
  },
  pancreas: {
    label: 'Glycemia anchor, islet and exocrine modifiers',
    detail:
      'The only organ with two functions scored: glycemia anchors islet function, and fecal elastase bounds exocrine function. A patient can lose one and keep the other.',
  },
  gonad: {
    label: 'Sex-specific anchor, bounded modifiers',
    detail:
      'Confirmed morning testosterone (testis) or cycle and menopausal status (ovary) sets the base. Age-appropriate menopause is scored as a normal physiological state, not a deficit. Symptom burden and bone density are bounded modifiers.',
  },
};

/** The primary parameter that anchors each organ — named so the detail view can
 * say which single test the whole score hangs off. */
export const ORGAN_ANCHOR_PARAM: Record<Exclude<OrganName, 'overall'>, string> = {
  kidney: 'egfr',
  liver: 'synthetic_points',
  lung: 'fev1_pct',
  heart: 'lvef',
  brain: 'moca',
  thyroid: 'tsh+ft4',
  adrenal: 'cortisol_reserve',
  pituitary: 'axis_integrity',
  pancreas: 'hba1c',
  gonad: 'sex_specific',
};

export interface DerivationStep {
  step: string;
  detail: string;
  result: string;
  /** Optional visual emphasis for the row (cap = danger, band = the outcome). */
  emphasis?: 'cap' | 'band';
  /** Dimmed informational row (a value that is NOT on record). */
  muted?: boolean;
}

const round1 = (n: number) => Number(n.toFixed(1));

/** Compact "value unit (date)" for a persisted input, or null when not stored. */
export function inputValueText(organ: OrganScore, key: string): string | null {
  const v = organ.structured_fields?.inputs?.[key];
  if (!v || typeof v.value !== 'number') return null;
  const meta = PARAM_META[key];
  const unit = meta?.unit ? ` ${meta.unit}` : '';
  const date = v.date ? ` · ${String(v.date).slice(0, 10)}` : '';
  return `${v.value}${unit}${date}`;
}

/** The organ's parameters and what each one's ABSENCE means — the scorer never
 * guesses a missing value (it lowers confidence instead), and the derivation
 * must say so explicitly or the score is untraceable for sparse patients. */
export const ORGAN_PARAM_ROLES: Record<
  Exclude<OrganName, 'overall'>,
  { key: string; role: 'axis' | 'penalty'; missing: string }[]
> = {
  kidney: [
    { key: 'egfr', role: 'axis', missing: 'Kidney cannot be staged without eGFR.' },
    {
      key: 'uacr',
      role: 'penalty',
      missing: 'No albuminuria modifier applied — this is NOT a normal (A1) result; confidence is lowered instead of guessing.',
    },
  ],
  liver: [
    { key: 'albumin', role: 'axis', missing: 'Synthetic points need all three markers — albumin is missing, so the liver is not staged.' },
    { key: 'inr', role: 'axis', missing: 'Synthetic points need all three markers — INR is missing, so the liver is not staged.' },
    { key: 'bilirubin', role: 'axis', missing: 'Synthetic points need all three markers — bilirubin is missing, so the liver is not staged.' },
  ],
  lung: [
    { key: 'fev1_pct', role: 'axis', missing: 'Lung severity cannot be graded without FEV1 % predicted.' },
    { key: 'spo2', role: 'penalty', missing: 'SpO₂ modifier skipped — confidence lowered instead of guessing.' },
    { key: 'dlco_pct', role: 'penalty', missing: 'DLCO modifier skipped — gas exchange is not characterized.' },
  ],
  heart: [
    { key: 'lvef', role: 'axis', missing: 'Heart cannot be anchored without an LVEF.' },
    { key: 'nt_probnp', role: 'penalty', missing: 'No natriuretic-peptide modifier — wall stress is not characterized.' },
    { key: 'nyha', role: 'penalty', missing: 'No NYHA class on record — functional limitation is not captured.' },
  ],
  brain: [
    { key: 'moca', role: 'axis', missing: 'Brain cannot be anchored without a standardized cognitive scale.' },
    { key: 'mrs', role: 'penalty', missing: 'No functional-status modifier — disability impact is not captured.' },
  ],
  thyroid: [
    { key: 'tsh', role: 'axis', missing: 'TSH and free T4 are read as a pair — without TSH the axis pattern cannot be set and the thyroid is not staged.' },
    { key: 'ft4', role: 'axis', missing: 'TSH and free T4 are read as a pair — without free T4 the axis pattern cannot be set and the thyroid is not staged.' },
  ],
  adrenal: [
    { key: 'cortisol_8am', role: 'axis', missing: 'Adrenal reserve needs a MORNING cortisol or a stimulated peak. A random cortisol is uninterpretable and is deliberately not used.' },
    { key: 'acth', role: 'penalty', missing: 'Without paired ACTH the primary-vs-secondary pattern is unknown — confidence is lowered rather than assuming a primary defect.' },
  ],
  pituitary: [
    { key: 'pituitary_axes_assessed', role: 'axis', missing: 'The pituitary is scored by how many axes are intact; with no axis assessment it is not staged. An unassessed axis is UNKNOWN, never counted as intact.' },
  ],
  pancreas: [
    { key: 'hba1c', role: 'axis', missing: 'Islet function cannot be anchored without HbA1c, so the pancreas is not staged.' },
    { key: 'fecal_elastase', role: 'penalty', missing: 'No exocrine modifier applied — this is NOT evidence of preserved acinar function; confidence is lowered instead of guessing.' },
  ],
  gonad: [
    { key: 'total_testosterone', role: 'axis', missing: 'Needs a morning testosterone confirmed on a second sample — a single or untimed value cannot establish hypogonadism.' },
    { key: 'dxa_t_score', role: 'penalty', missing: 'No bone modifier applied — absence of a DXA is not a normal DXA; confidence is lowered instead.' },
  ],
};

/**
 * The combine step's arithmetic, spelled out to match the methodology's worked
 * examples ("eGFR 51 − albuminuria 11 = 40"; "worst axis 72, − penalties = 55")
 * — reconstructed only from persisted values (sub-scores, penalties_applied,
 * base). Every case is explicit — including "no penalty because the test is not
 * on record" and "penalty 0 because the value is below threshold" — so the
 * arithmetic is never a bare restatement of the same number.
 */
function combineDetail(organ: OrganScore, fallback: string, base: number): string {
  const sf = organ.structured_fields;
  const b = Math.round(base);

  // Rubric v0.10 (anchor_v1): read the arithmetic straight off the persisted
  // anchor + modifiers, so the line the clinician sees is the line the scorer
  // computed rather than a frontend reconstruction of it.
  const fn = sf?.function;
  if (fn?.anchor) {
    const { stage, base: anchorBase } = fn.anchor;
    const mods = Object.entries(fn.modifiers ?? {}).filter(([, m]) => m.points !== 0);
    if (mods.length === 0) {
      return `${stage} anchors at ${Math.round(anchorBase)}; no modifiers applied = ${b}.`;
    }
    const terms = mods
      .map(([key, m]) => {
        const label = SUBSCORE_LABEL[key] ?? key.replace(/_/g, ' ');
        const sign = m.points < 0 ? '−' : '+';
        return `${sign} ${label} ${Math.abs(round1(m.points))}`;
      })
      .join(' ');
    return `${stage} anchors at ${Math.round(anchorBase)} ${terms} = ${b}.`;
  }

  // Legacy rows (rubric <= 0.6, transfer_v1) keep their original wording.
  if (organ.organ === 'kidney') {
    const egfr = sf?.subscores?.egfr;
    const pen = sf?.penalties_applied?.uacr;
    const hasUacr = typeof sf?.inputs?.uacr?.value === 'number' || typeof pen === 'number';
    if (typeof egfr === 'number' && typeof pen === 'number' && pen > 0) {
      return `eGFR ${Math.round(egfr)} − albuminuria penalty ${round1(pen)} = ${b}.`;
    }
    if (typeof egfr === 'number' && hasUacr) {
      return `UACR below the 30 mg/g threshold — penalty 0; the eGFR score carries through = ${b}.`;
    }
    if (typeof egfr === 'number') {
      return `No UACR on record — no albuminuria penalty applied; the eGFR score carries through = ${b}.`;
    }
  } else if (organ.organ === 'lung') {
    const fev1 = sf?.subscores?.fev1_pct;
    const pen = sf?.penalties_applied?.spo2;
    const hasSpo2 = typeof sf?.inputs?.spo2?.value === 'number' || typeof pen === 'number';
    if (typeof fev1 === 'number' && typeof pen === 'number' && pen > 0) {
      return `FEV1 ${Math.round(fev1)} − SpO₂ penalty ${round1(pen)} = ${b}.`;
    }
    if (typeof fev1 === 'number' && hasSpo2) {
      return `SpO₂ in the normal range — penalty 0; the FEV1 score carries through = ${b}.`;
    }
    if (typeof fev1 === 'number') {
      return `No SpO₂ on record — no penalty applied; the FEV1 score carries through = ${b}.`;
    }
  } else if (organ.organ === 'liver') {
    const entries = Object.entries(sf?.subscores ?? {});
    if (entries.length === 1) {
      const only = SUBSCORE_LABEL[entries[0][0]] ?? entries[0][0];
      return `Only ${only} is scored — worst-of over a single axis = ${b}. The two missing axes lower confidence, not the score.`;
    }
    if (entries.length > 1) {
      const min = Math.round(Math.min(...entries.map(([, v]) => v)));
      const drop = min - b;
      return drop > 0
        ? `Weakest axis ${min}, minus small penalties ${drop} = ${b}.`
        : `The weakest of the ${entries.length} scored axes leads = ${b}.`;
    }
  }
  return fallback;
}

/**
 * One compact formula line with this patient's real numbers substituted —
 * "how the number was made", at a glance, per the methodology:
 * score = clamp( min( base, caps ) ), base = curve(value) − penalties.
 */
export function scoreFormula(organ: OrganScore): string | null {
  if (organ.organ === 'overall' || organ.score == null) return null;
  const sf = organ.structured_fields;
  if (!sf) return null;

  // Rubric v0.10: the headline is function minus a bounded risk penalty, so the
  // formula has to show BOTH tracks. Showing only the function arithmetic would
  // leave the clinician unable to reconcile the line with the number above it.
  const fn = sf.function;
  if (fn?.anchor) {
    const raw = sf.inputs?.[fn.anchor.param]?.value;
    const anchorText =
      typeof raw === 'number'
        ? `${SUBSCORE_LABEL[fn.anchor.param] ?? fn.anchor.param} ${raw} → ${fn.anchor.stage} = ${Math.round(fn.anchor.base)}`
        : `${fn.anchor.stage} = ${Math.round(fn.anchor.base)}`;
    const modText = Object.entries(fn.modifiers ?? {})
      .filter(([, m]) => m.points !== 0)
      .map(([key, m]) => `${m.points < 0 ? '−' : '+'} ${SUBSCORE_LABEL[key] ?? key} ${Math.abs(round1(m.points))}`)
      .join(' ');
    let formula = modText ? `${anchorText} ${modText}` : anchorText;
    if (Array.isArray(sf.caps_applied) && sf.caps_applied.length > 0) {
      formula += ` → capped at ${Math.round(Math.min(...sf.caps_applied))}`;
    }
    if (typeof organ.function_score === 'number') formula += ` → function ${organ.function_score}`;
    if (sf.capped_by_red_flag) {
      formula += ' · risk penalty suppressed (acute flag governs)';
    } else if (typeof sf.risk_penalty === 'number' && sf.risk_penalty > 0) {
      formula += ` − risk penalty ${sf.risk_penalty}`;
    } else {
      formula += ' · no risk penalty';
    }
    return `${formula} → score ${organ.score}`;
  }

  // Legacy rows (rubric <= 0.6, transfer_v1).
  const parts: string[] = [];
  const subs = Object.entries(sf.subscores ?? {});
  for (const [key, v] of subs) {
    const raw = sf.inputs?.[key]?.value;
    const label = SUBSCORE_LABEL[key] ?? key;
    parts.push(typeof raw === 'number' ? `curve(${label} ${raw}) = ${Math.round(v)}` : `curve(${label}) = ${Math.round(v)}`);
  }
  const pens = Object.entries(sf.penalties_applied ?? {}).filter(([, v]) => typeof v === 'number');
  for (const [key, v] of pens) parts.push(`− ${key === 'uacr' ? 'albuminuria' : key} ${round1(v as number)}`);
  let formula = parts.join(' · ');
  if (typeof sf.base === 'number') formula += ` → base ${Math.round(sf.base)}`;
  if (Array.isArray(sf.caps_applied) && sf.caps_applied.length > 0) {
    formula += ` → capped at ${Math.round(Math.min(...sf.caps_applied))}`;
  } else {
    formula += ' · no red-flag caps';
  }
  formula += ` → score ${organ.score}`;
  return formula;
}

/**
 * One always-visible, plain-language sentence naming the CLINICAL reason this
 * patient got this score — the single most important value, plus the cap or the
 * missing data that shaped it. Built only from persisted data; returns null when
 * there is nothing honest to say (Overall roll-up, no structured_fields, or an
 * older row without inputs). This is the "why" a clinician reads without
 * expanding the derivation.
 */
export function scoreReason(organ: OrganScore): string | null {
  if (organ.organ === 'overall' || organ.score == null || organ.band === 'unknown') return null;
  const sf = organ.structured_fields;
  if (!sf) return null;

  // Red-flag cap dominates the story — say so first.
  if (isRedFlagCapped(organ)) {
    const flags = (organ.red_flags ?? []).map(redFlagLabel).join(', ');
    const ceiling = Array.isArray(sf.caps_applied) && sf.caps_applied.length > 0 ? Math.round(Math.min(...sf.caps_applied)) : organ.score;
    return flags
      ? `Capped at ${ceiling} by an acute red flag — ${flags} — which overrides the chronic picture.`
      : `Capped at ${ceiling} by an acute red flag that overrides the chronic picture.`;
  }

  // The driving axis: for worst-of organs it's the weakest; for subtract organs
  // it's the single transfer input. Name it with the patient's real value.
  const subs = subscoreEntries(organ);
  if (subs.length === 0) return null;
  const driver = WORST_OF_ORGANS.has(organ.organ)
    ? subs.reduce((lo, e) => (e.value < lo.value ? e : lo), subs[0])
    : subs[0];
  const driverValue = inputValueText(organ, driver.key);
  const lead = driverValue
    ? `Driven by ${driver.label} ${driverValue}`
    : `Driven by the ${driver.label} sub-score (${driver.value})`;

  // What shaped it beyond the driver: a real penalty, or missing data.
  const clauses: string[] = [];
  const appliedPenalty = Object.entries(sf.penalties_applied ?? {}).find(([, v]) => typeof v === 'number' && v > 0);
  if (appliedPenalty) {
    const [pk, pv] = appliedPenalty;
    const penaltyName = pk === 'uacr' ? 'albuminuria' : pk === 'spo2' ? 'SpO₂' : pk;
    clauses.push(`with an ${penaltyName} penalty of ${round1(pv as number)}`);
  }
  const roles = ORGAN_PARAM_ROLES[organ.organ as 'kidney' | 'liver' | 'lung'] ?? [];
  const missing = sf.inputs
    ? roles
        .filter(
          (p) =>
            typeof sf.inputs?.[p.key]?.value !== 'number' &&
            typeof sf.subscores?.[p.key] !== 'number' &&
            typeof sf.penalties_applied?.[p.key] !== 'number',
        )
        .map((p) => PARAM_META[p.key]?.label ?? p.key)
    : [];
  if (missing.length > 0) {
    clauses.push(`${missing.join(' and ')} not on record, so confidence is lowered rather than the score`);
  }

  const tail = clauses.length > 0 ? `, ${clauses.join('; ')}` : '';
  return `${lead}${tail}. ${bandStyle(organ.band).meaning}`;
}

/**
 * The full derivation chain for ONE scored organ, in reading order:
 * (lung gate →) sub-scores → combine/base → red-flag cap → band. Built only
 * from persisted structured_fields; returns [] when there's nothing honest to
 * show (no structured_fields, or a non-scored organ). Overall uses
 * `overallContributions` instead — its story is a roll-up, not a lab chain.
 */
export function deriveScoreSteps(organ: OrganScore): DerivationStep[] {
  if (organ.organ === 'overall') return [];
  const sf = organ.structured_fields;
  if (!sf || organ.score == null || organ.band === 'unknown') return [];
  const steps: DerivationStep[] = [];

  // Lung only: the obstruction gate had to pass for a score to exist at all.
  if (organ.organ === 'lung') {
    steps.push({
      step: 'Obstruction gate',
      detail: 'FEV1/FVC below 0.70 — the COPD scoring path applies.',
      result: 'Passed',
    });
  }

  const fn = sf.function;
  if (fn?.anchor) {
    // Rubric v0.10: the anchor row first (the one parameter the whole score
    // hangs off), then one row per applied modifier. Without these the detail
    // panel would show only a bare final number, which is precisely the
    // untraceable score this view exists to prevent.
    const anchorValue = inputValueText(organ, fn.anchor.param);
    const anchorLabel = PARAM_META[fn.anchor.param]?.label ?? SUBSCORE_LABEL[fn.anchor.param] ?? fn.anchor.param;
    steps.push({
      step: anchorValue ? `${anchorLabel} ${anchorValue}` : `Anchor · ${anchorLabel}`,
      detail:
        `${fn.anchor.stage} on the staging table sets the base score.` +
        (anchorValue ? '' : ' Recorded value was not stored with this score — Rescore to capture it.'),
      result: `→ ${Math.round(fn.anchor.base)}`,
    });
    for (const [key, mod] of Object.entries(fn.modifiers ?? {})) {
      const modValue = inputValueText(organ, key);
      const modLabel = PARAM_META[key]?.label ?? SUBSCORE_LABEL[key] ?? key.replace(/_/g, ' ');
      steps.push({
        step: modValue ? `${modLabel} ${modValue}` : `Modifier · ${modLabel}`,
        detail: `${mod.band} — a bounded modifier, applied to the base anchor.`,
        result: mod.points === 0 ? 'no change' : `${mod.points < 0 ? '−' : '+'}${Math.abs(round1(mod.points))}`,
      });
    }
  } else {
    for (const e of subscoreEntries(organ)) {
      const valueText = inputValueText(organ, e.key);
      steps.push({
        // The measured value and its date ARE the step — "eGFR 54 mL/min/1.73m² ·
        // 2026-03-14 → 66" is traceable; a bare "eGFR → 66" is not.
        step: valueText
          ? `${e.label} ${valueText}${e.leads ? ' (leads)' : ''}`
          : `Sub-score · ${e.label}${e.leads ? ' (leads)' : ''}`,
        detail:
          (SUBSCORE_MEANING[e.key] ?? 'Parameter score on its transfer curve.') +
          (valueText ? '' : ' Recorded value was not stored with this score — Rescore to capture it.'),
        result: `→ ${e.value}`,
      });
    }
  }

  // Every parameter this organ scores from that is NOT on record gets its own
  // dimmed row — a sparse patient's score is untraceable without them, and
  // "missing lowers confidence, never the score" is the methodology's rule.
  const roles = ORGAN_PARAM_ROLES[organ.organ as Exclude<OrganName, 'overall'>] ?? [];
  if (sf.inputs) {
    for (const p of roles) {
      const present =
        typeof sf.inputs?.[p.key]?.value === 'number' ||
        typeof sf.subscores?.[p.key] === 'number' ||
        typeof sf.penalties_applied?.[p.key] === 'number' ||
        typeof sf.function?.modifiers?.[p.key]?.points === 'number' ||
        sf.function?.anchor?.param === p.key;
      if (!present) {
        steps.push({
          step: `${PARAM_META[p.key]?.label ?? p.key} — not on record`,
          detail: p.missing,
          result: '—',
          muted: true,
        });
      }
    }
  }

  const method = COMBINE_METHOD[organ.organ as Exclude<OrganName, 'overall'>];
  if (typeof sf.base === 'number' && method) {
    steps.push({ step: method.label, detail: combineDetail(organ, method.detail, sf.base), result: String(Math.round(sf.base)) });
  }

  if (Array.isArray(sf.caps_applied) && sf.caps_applied.length > 0) {
    const flags = (organ.red_flags ?? []).map(redFlagLabel).join(', ');
    steps.push({
      step: `Red-flag cap${flags ? ` · ${flags}` : ''}`,
      detail: 'An acute finding sets a hard ceiling — not averaged against the chronic picture.',
      result: String(Math.round(Math.min(...sf.caps_applied))),
      emphasis: 'cap',
    });
  }

  // Rubric v0.10: the risk track's contribution to the headline. Shown even
  // when it is zero, because "no penalty" and "no risk track" are different
  // facts and the arithmetic has to close — a clinician reading function 85
  // above a headline of 74 needs the missing 11 accounted for.
  if (typeof organ.function_score === 'number' && typeof sf.risk_penalty === 'number') {
    const riskBand = organ.risk_band && organ.risk_band !== 'unknown' ? RISK_BAND_LABEL[organ.risk_band] : null;
    steps.push({
      step: `Modifiable risk${riskBand ? ` · ${riskBand}` : ''}`,
      detail: sf.capped_by_red_flag
        ? 'An acute red flag capped the function score, so no risk penalty is applied — the acute state governs.'
        : organ.risk_score == null
          ? 'No modifiable risk factors on record, so no penalty is applied — missing data never lowers the score.'
          : `Risk score ${organ.risk_score}/100 (higher is better care) applies a bounded, downward-only trajectory penalty.`,
      result: sf.risk_penalty > 0 ? `−${sf.risk_penalty}` : 'no change',
    });
  }

  const range = bandRange(organ.band);
  steps.push({
    step: 'Band',
    detail: range
      ? `${organ.score} falls in the ${bandStyle(organ.band).label} range (${range.from}–${range.to}).`
      : bandStyle(organ.band).meaning,
    result: bandStyle(organ.band).label,
    emphasis: 'band',
  });
  return steps;
}

/** Human label + unit for each raw clinical value the scorer can consume. */
export const PARAM_META: Record<string, { label: string; unit?: string }> = {
  egfr: { label: 'eGFR', unit: 'mL/min/1.73m²' },
  uacr: { label: 'UACR', unit: 'mg/g' },
  potassium: { label: 'Potassium', unit: 'mmol/L' },
  bicarbonate: { label: 'Bicarbonate', unit: 'mmol/L' },
  albumin: { label: 'Albumin', unit: 'g/dL' },
  inr: { label: 'INR' },
  bilirubin: { label: 'Bilirubin', unit: 'mg/dL' },
  platelets: { label: 'Platelets', unit: '×10⁹/L' },
  fev1_pct: { label: 'FEV1', unit: '% pred' },
  fev1_fvc_ratio: { label: 'FEV1/FVC' },
  spo2: { label: 'SpO₂', unit: '%' },
  dlco_pct: { label: 'DLCO', unit: '% pred' },
  pao2: { label: 'PaO₂', unit: 'mmHg' },
  paco2: { label: 'PaCO₂', unit: 'mmHg' },
  // heart
  lvef: { label: 'LVEF', unit: '%' },
  nt_probnp: { label: 'NT-proBNP', unit: 'pg/mL' },
  nyha: { label: 'NYHA class' },
  // brain — scale scores carry their maximum so a bare "21" is readable.
  moca: { label: 'MoCA', unit: '/ 30' },
  mrs: { label: 'mRS', unit: '/ 6' },
  gcs: { label: 'GCS', unit: '/ 15' },
};

/** Human label for a coded finding id (e.g. 'ecg_changes' → 'ECG changes'). */
export const FINDING_LABEL: Record<string, string> = {
  ecg_changes: 'ECG changes',
  hyperkalemia_ecg: 'Hyperkalemia on ECG',
  acidosis: 'Acidosis',
  acidosis_symptomatic: 'Symptomatic acidosis',
  oliguria: 'Oliguria',
  hydronephrosis: 'Hydronephrosis',
  new_obstruction: 'New obstruction',
  ascites: 'Ascites',
  hepatic_encephalopathy: 'Hepatic encephalopathy',
  variceal_bleeding: 'Variceal bleeding',
  nonobstructive_jaundice: 'Non-obstructive jaundice',
  sbp: 'Spontaneous bacterial peritonitis',
  hepatorenal_syndrome: 'Hepatorenal syndrome',
  cirrhotic_morphology: 'Cirrhotic morphology',
  nodular_liver: 'Nodular liver',
  anticoagulant: 'On anticoagulant',
  acute: 'Acute presentation',
  dlco_reduced: 'Reduced DLCO',
  severe_exacerbation: 'Severe exacerbation',
  ed_or_hospitalization: 'ED visit / hospitalization',
  gate_unconfirmed: 'Obstruction gate unconfirmed',
  unconfirmed_primary: 'Unconfirmed primary',
};

export function findingLabel(id: string): string {
  return FINDING_LABEL[id] ?? id.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

/** X-axis caption per transfer test (which way is worse), for the curves. */
export const CURVE_AXIS: Record<string, string> = {
  egfr: 'eGFR — lower is worse',
  albumin: 'albumin g/dL — higher is better',
  inr: 'INR — higher is worse',
  bilirubin: 'bilirubin mg/dL — higher is worse',
  fev1_pct: 'FEV1 % predicted — higher is better',
};

export interface TransferCurveDatum {
  key: string;
  label: string;
  xLabel: string;
  /** The scorer's piecewise-linear knots ([value, score]) for this test. */
  anchors: { x: number; y: number }[];
  /** This patient's point: raw value → real sub-score. Sits on the curve.
   * Null when the raw value wasn't stored with the score (older rows) — the
   * curve still renders so the mapping is always visible, instead of the whole
   * chart silently vanishing. */
  patient: { x: number; y: number } | null;
  /** The persisted sub-score, shown even when the raw value is missing. */
  subScore: number;
}

/**
 * Pair each transfer sub-score with its published curve and (when stored) the
 * patient's real point, so the UI can plot "eGFR 38 → 51" on the actual
 * function. A sub-score whose raw value wasn't persisted (scores written before
 * input capture shipped) still gets its curve — previously those charts
 * vanished entirely, which read as "no curves for liver".
 */
export function transferCurveData(
  organ: OrganScore,
  transfers: Record<string, { anchors: [number, number][] }> | undefined,
): TransferCurveDatum[] {
  const subs = organ.structured_fields?.subscores;
  const inputs = organ.structured_fields?.inputs;
  if (!subs || !transfers) return [];
  const out: TransferCurveDatum[] = [];
  for (const [key, score] of Object.entries(subs)) {
    const curve = transfers[key];
    if (!curve) continue;
    const raw = inputs?.[key]?.value;
    out.push({
      key,
      label: SUBSCORE_LABEL[key] ?? key.replace(/_/g, ' '),
      xLabel: CURVE_AXIS[key] ?? SUBSCORE_LABEL[key] ?? key,
      anchors: curve.anchors.map(([x, y]) => ({ x, y })),
      subScore: Math.round(score),
      patient: typeof raw === 'number' ? { x: raw, y: Math.round(score) } : null,
    });
  }
  return out;
}

/** The scorer's penalty-curve definitions (from the method-curves endpoint). */
export interface PenaltyCurves {
  uacr: { kind: 'log'; threshold: number; k: number; cap: number };
  spo2: { kind: 'knots'; anchors: [number, number][] };
}

export interface PenaltyCurveDatum {
  key: 'uacr' | 'spo2';
  label: string;
  xLabel: string;
  scale: 'log' | 'linear';
  xDomain: [number, number];
  /** Clean x tick values (a log axis otherwise labels raw sample floats). */
  xTicks: number[];
  yMax: number;
  /** Densely sampled curve (value → penalty) so the plotted point sits on it. */
  curve: { x: number; y: number }[];
  /** This patient's point: raw value → the persisted penalty subtracted. */
  patient: { x: number; y: number };
}

/**
 * Pair each bounded penalty (kidney albuminuria, lung hypoxaemia) with its real
 * curve and the patient's point (raw value → the persisted penalty). The curve
 * is sampled from the scorer's own penalty definition so the point lands on it.
 * Returns [] when the value or penalty wasn't recorded.
 */
export function penaltyCurveData(
  organ: OrganScore,
  penalties: PenaltyCurves | undefined,
): PenaltyCurveDatum[] {
  const inputs = organ.structured_fields?.inputs;
  const applied = organ.structured_fields?.penalties_applied;
  if (!inputs || !applied || !penalties) return [];
  const out: PenaltyCurveDatum[] = [];

  const uacrVal = inputs.uacr?.value;
  if (typeof uacrVal === 'number' && typeof applied.uacr === 'number') {
    const p = penalties.uacr;
    // Log-spaced samples across the reporting range; penalty = 0 below the
    // threshold, then min(k·log10(v/threshold), cap).
    const curve: { x: number; y: number }[] = [];
    const lo = 10;
    const hi = 3000;
    const n = 48;
    for (let i = 0; i <= n; i++) {
      const x = lo * (hi / lo) ** (i / n);
      const y = x < p.threshold ? 0 : Math.min(p.k * Math.log10(x / p.threshold), p.cap);
      curve.push({ x, y });
    }
    out.push({
      key: 'uacr',
      label: 'UACR penalty',
      xLabel: 'UACR mg/g — log scale',
      scale: 'log',
      xDomain: [lo, hi],
      xTicks: [30, 100, 300, 1000, 3000],
      yMax: p.cap,
      curve,
      patient: { x: uacrVal, y: applied.uacr },
    });
  }

  const spo2Val = inputs.spo2?.value;
  if (typeof spo2Val === 'number' && typeof applied.spo2 === 'number') {
    const knots = penalties.spo2.anchors;
    const curve = knots.map(([x, y]) => ({ x, y }));
    const yMax = Math.max(...knots.map(([, y]) => y));
    out.push({
      key: 'spo2',
      label: 'SpO₂ penalty',
      xLabel: 'resting SpO₂ %',
      scale: 'linear',
      xDomain: [knots[0][0], knots[knots.length - 1][0]],
      xTicks: [84, 88, 92, 96, 100],
      yMax,
      curve,
      patient: { x: spo2Val, y: applied.spo2 },
    });
  }
  return out;
}

/** Human label for an acute trend signal the red-flag caps consumed. */
export const TREND_LABEL: Record<string, string> = {
  aki: 'Acute kidney injury — rising creatinine',
  aki_severe: 'Severe acute kidney injury',
  spo2_drop: 'Acute desaturation — SpO₂ dropping',
};

export function trendLabel(id: string): string {
  return TREND_LABEL[id] ?? id.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

export interface ClinicalInput {
  key: string;
  label: string;
  value: number;
  unit?: string;
  date?: string;
}

/** Boolean/demographic scorer parameters — context, not measurements. Rendering
 * these in the Measurements list produced entries like a bare "is male". */
const CONTEXT_PARAMS: Record<string, (v: unknown) => string | null> = {
  is_male: (v) => (v ? 'Male' : 'Female'),
  on_dialysis: (v) => (v ? 'On dialysis' : null),
  transplant: (v) => (v ? 'Transplant recipient' : null),
};

/** The raw clinical values behind a score, in a stable order, human-labelled.
 * Context flags (sex, dialysis) are excluded — see contextInputs. Returns []
 * when the backend didn't persist inputs (older scores). */
export function clinicalInputs(organ: OrganScore): ClinicalInput[] {
  const inputs = organ.structured_fields?.inputs;
  if (!inputs || Object.keys(inputs).length === 0) return [];
  return Object.entries(inputs)
    .filter(([key, v]) => !(key in CONTEXT_PARAMS) && typeof v?.value === 'number')
    .map(([key, v]) => {
      const meta = PARAM_META[key];
      return {
        key,
        label: meta?.label ?? key.replace(/_/g, ' '),
        value: v.value,
        unit: meta?.unit,
        date: v.date,
      };
    });
}

/** Demographic/context flags the scorer consumed, as readable chips
 * ("Male", "On dialysis") — never mixed into the measurements list. */
export function contextInputs(organ: OrganScore): string[] {
  const inputs = organ.structured_fields?.inputs;
  if (!inputs) return [];
  const out: string[] = [];
  for (const [key, fmt] of Object.entries(CONTEXT_PARAMS)) {
    if (key in inputs) {
      const label = fmt((inputs as Record<string, { value: unknown }>)[key]?.value);
      if (label) out.push(label);
    }
  }
  return out;
}

/** How completely an input the score model consumes is on record.
 *  - captured:     raw value (+ date) persisted; we can show where it landed.
 *  - scored_no_raw: it drove a persisted sub-score/penalty, but the raw value
 *                   itself wasn't stored (rows written before input capture) —
 *                   a Rescore recovers it.
 *  - missing:      not on record at all; the scorer never guesses, so this
 *                  lowered confidence, not the score. */
export type InputStatus = 'captured' | 'scored_no_raw' | 'missing';

export interface InputInventoryEntry {
  key: string;
  label: string;
  unit?: string;
  role: 'axis' | 'penalty';
  status: InputStatus;
  /** Raw value + date — present only when status is 'captured'. */
  value?: number;
  date?: string;
  /** Provenance note when the value was COMPUTED, not read from the chart
   *  (e.g. eGFR derived from creatinine via CKD-EPI 2021). */
  derived?: string;
  /** Where this input landed in the math, in words ("→ sub-score 82",
   *  "→ −11 penalty", "no penalty (below threshold)"). */
  landed?: string;
  /** When missing: the exact clinical consequence of its absence. */
  consequence?: string;
}

/**
 * The COMPLETE set of clinical inputs this organ's score model can consume,
 * each resolved to one honest status (see InputStatus). This is the "which data
 * went into the score — and which data it could have used but didn't" inventory.
 * It's derivable for any scored organ (it reads the fixed ORGAN_PARAM_ROLES, not
 * just what was persisted), so the card can always show the full picture instead
 * of looking empty when a raw value wasn't captured. Overall roll-up returns [].
 */
export function scoreInputInventory(organ: OrganScore): InputInventoryEntry[] {
  if (organ.organ === 'overall') return [];
  const roles = ORGAN_PARAM_ROLES[organ.organ as 'kidney' | 'liver' | 'lung'];
  if (!roles) return [];
  const sf = organ.structured_fields;
  const subs = sf?.subscores ?? {};
  const pens = sf?.penalties_applied ?? {};
  const inputs = sf?.inputs ?? {};
  return roles.map((r): InputInventoryEntry => {
    const meta = PARAM_META[r.key];
    const raw = inputs[r.key];
    const hasRaw = typeof raw?.value === 'number';
    const label = meta?.label ?? r.key.replace(/_/g, ' ');
    const unit = meta?.unit;
    if (r.role === 'axis') {
      const sub = subs[r.key];
      if (typeof sub === 'number') {
        const landed = `→ sub-score ${Math.round(sub)}`;
        return hasRaw
          ? { key: r.key, label, unit, role: 'axis', status: 'captured', value: raw!.value, date: raw!.date, derived: raw!.derived, landed }
          : { key: r.key, label, unit, role: 'axis', status: 'scored_no_raw', landed };
      }
      return { key: r.key, label, unit, role: 'axis', status: 'missing', consequence: r.missing };
    }
    // penalty
    const pen = pens[r.key];
    const landed = typeof pen === 'number' && pen > 0 ? `→ −${round1(pen)} penalty` : 'no penalty (below threshold)';
    if (hasRaw) {
      return { key: r.key, label, unit, role: 'penalty', status: 'captured', value: raw!.value, date: raw!.date, landed };
    }
    if (typeof pen === 'number') {
      return { key: r.key, label, unit, role: 'penalty', status: 'scored_no_raw', landed };
    }
    return { key: r.key, label, unit, role: 'penalty', status: 'missing', consequence: r.missing };
  });
}

/** What each supporting (non-axis/penalty) input the scorer weighs actually
 * feeds — the red-flag checks and the lung obstruction gate (scorer.py:
 * detect_red_flags / combine). These never move the base score directly; they
 * can cap it (red flags) or decide whether the organ is gradeable at all
 * (the gate), so the row explains why the value is on the card. */
export const SUPPORTING_PARAM_ROLE: Record<string, string> = {
  potassium: 'Hyperkalemia red-flag check',
  bicarbonate: 'Severe-acidosis red-flag check',
  platelets: 'Cirrhotic-morphology red-flag check',
  fev1_fvc_ratio: 'Obstruction gate — must be < 0.70 to score',
  pao2: 'Acute-respiratory-failure red-flag check',
  paco2: 'Acute-respiratory-failure red-flag check',
};

export interface SupportingInput {
  key: string;
  label: string;
  value: number;
  unit?: string;
  date?: string;
  role: string;
}

/** Every persisted numeric value the scorer saw that ISN'T a scoring axis or
 * penalty (those are scoreInputInventory) and isn't a context flag (those are
 * contextInputs) — the supporting labs / gate values that drive red-flag caps
 * and the lung gate (potassium, bicarbonate, platelets, FEV1/FVC, blood gases).
 * Present-only: unlike the axes/penalties there is no fixed "expected" set to
 * mark as missing, so we only show what was actually on record. Returns [] for
 * the Overall roll-up and when none were captured. This is the rest of the
 * data that fed the score — surfaced so nothing the scorer weighed is hidden. */
export function supportingInputs(organ: OrganScore): SupportingInput[] {
  if (organ.organ === 'overall') return [];
  const inputs = organ.structured_fields?.inputs;
  if (!inputs) return [];
  const roles = ORGAN_PARAM_ROLES[organ.organ as 'kidney' | 'liver' | 'lung'] ?? [];
  const modelKeys = new Set(roles.map((r) => r.key));
  const out: SupportingInput[] = [];
  for (const [key, v] of Object.entries(inputs)) {
    if (modelKeys.has(key) || key in CONTEXT_PARAMS || typeof v?.value !== 'number') continue;
    const meta = PARAM_META[key];
    out.push({
      key,
      label: meta?.label ?? key.replace(/_/g, ' '),
      value: v.value,
      unit: meta?.unit,
      date: v.date,
      role: SUPPORTING_PARAM_ROLE[key] ?? 'Supporting measurement',
    });
  }
  return out;
}

export interface OverallContribution {
  organ: OrganName;
  label: string;
  score: number;
  band: OrganBand;
  /** The weakest graded organ — the one the overall is anchored on. */
  isAnchor: boolean;
}

/**
 * The overall roll-up's derivation: every graded organ with its score/band,
 * the weakest marked as the anchor (overall = min, never a mean — see
 * scorer.compute_overall_score). Reads structured_fields.organ_scores /
 * anchored_on; returns [] when the backend didn't send them.
 */
export function overallContributions(organ: OrganScore): OverallContribution[] {
  const scores = organ.structured_fields?.organ_scores;
  if (!scores || Object.keys(scores).length === 0) return [];
  const anchor = organ.structured_fields?.anchored_on;
  return Object.entries(scores)
    .map(([name, score]) => ({
      organ: name as OrganName,
      label: ORGAN_LABEL[name as OrganName] ?? name,
      score: Math.round(score),
      band: bandForScore(Math.round(score)),
      isAnchor: name === anchor,
    }))
    .sort((a, b) => a.score - b.score); // weakest first — the anchor leads
}
